import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import ArtworkCard from "@/components/public/ArtworkCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Gallery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [artworks, setArtworks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [years, setYears] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeTag = searchParams.get("tag") || "";
  const [filters, setFilters] = useState({ category: "all", year: "all", availability: "all", sort: "newest" });

  useEffect(() => {
    api.get("/artwork-categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/artworks/years").then(({ data }) => setYears(data)).catch(() => {});
    api.get("/artwork-tags").then(({ data }) => setTags(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { sort: filters.sort };
    if (filters.category !== "all") params.category = filters.category;
    if (filters.year !== "all") params.year = filters.year;
    if (filters.availability !== "all") params.availability = filters.availability;
    if (activeTag) params.tag = activeTag;
    api.get("/artworks", { params }).then(({ data }) => setArtworks(data)).catch(() => setArtworks([])).finally(() => setLoading(false));
  }, [filters, activeTag]);

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const toggleTag = (t) => {
    const next = new URLSearchParams(searchParams);
    if (activeTag === t) next.delete("tag"); else next.set("tag", t);
    setSearchParams(next);
  };

  return (
    <div className="on-dark violet-canvas fade-in flex-1 w-full">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12 sm:py-16">
        <SEO title="Galerie" description="Digitale Kunstgalerie von Susanne Monika Gysin – Gemälde, Bilder und Werke." />
        <header className="mb-10">
          <div className="text-xs uppercase tracking-[0.25em] text-[#F4C542] mb-4">Galerie</div>
          <h1 className="font-serif text-5xl sm:text-6xl font-light text-[#FFF9F2]">Werke</h1>
        </header>

        <div className="flex flex-wrap gap-3 mb-6 pb-6 border-b border-[#632A8E]/60">
          <FilterSelect testId="filter-category" value={filters.category} onChange={set("category")} placeholder="Kategorie"
            options={[{ v: "all", l: "Alle Kategorien" }, ...categories.map((c) => ({ v: c.name, l: c.name }))]} />
          <FilterSelect testId="filter-year" value={filters.year} onChange={set("year")} placeholder="Jahr"
            options={[{ v: "all", l: "Alle Jahre" }, ...years.map((y) => ({ v: String(y), l: String(y) }))]} />
          <FilterSelect testId="filter-availability" value={filters.availability} onChange={set("availability")} placeholder="Verfügbarkeit"
            options={[{ v: "all", l: "Alle" }, { v: "available", l: "Verfügbar" }, { v: "sold", l: "Verkauft" }]} />
          <FilterSelect testId="filter-sort" value={filters.sort} onChange={set("sort")} placeholder="Sortierung"
            options={[{ v: "newest", l: "Neueste" }, { v: "oldest", l: "Älteste" }, { v: "title", l: "Titel" }]} />
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-12" data-testid="gallery-tag-filter">
            {activeTag && (
              <button onClick={() => toggleTag(activeTag)} data-testid="gallery-tag-clear"
                className="rounded-full h-8 px-3 text-sm text-[#F4C542] border border-[#F4C542]">✕ Filter</button>
            )}
            {tags.map((t) => (
              <button
                key={t}
                onClick={() => toggleTag(t)}
                data-testid={`gallery-tag-${t}`}
                className={`rounded-full h-8 px-3.5 text-sm transition-colors ${activeTag === t ? "bg-[#F4C542] text-[#32104F]" : "text-[#D8B9EA] border border-[#632A8E] hover:border-[#F4C542]"}`}
              >
                #{t}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="text-[#D8B9EA]">Werke werden geladen…</p>
        ) : artworks.length ? (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-10">
            {artworks.map((a, i) => <ArtworkCard key={a.id} art={a} index={i} />)}
          </div>
        ) : (
          <div className="py-24 text-center">
            <p className="font-serif text-2xl text-[#D8B9EA]">Keine Werke gefunden</p>
            <p className="text-sm text-[#9A86B4] mt-2">Bitte passen Sie die Filter an.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="w-auto min-w-[150px] rounded-md border-[#632A8E] bg-[#3C1A5C] text-[#FFF9F2] h-11">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
